<template>
    <div class="w-100">
        <div class="card">
            <div class="card-header bg-primary text-white">
                <page-title :title="$t('participationRelationType.title')" sizeClass="h4"></page-title>
            </div>
            <div class="card-body">
                <div class="btn-toolbar justify-content-between mb-1" role="toolbar">
                    <div class="form-inline pl-2">
                        <button
                            v-if="hasCreateRights"
                            type="button"
                            class="btn btn-primary"
                            @click.stop="createBtnClick"
                        >{{ $t('buttons.create.btnText') }}</button>
                    </div>

                    <div class="input-group">
                        <input
                            v-model="searchQuery"
                            v-on:keyup.enter.stop="massSearch"
                            v-on:keyup.esc.stop="clearFilters"
                            type="text"
                            class="form-control"
                            aria-label="Mass search input"
                            aria-describedby="massSearch"
                        />
                        <button
                            type="button"
                            class="btn btn-secondary"
                            @click.stop="massSearch"
                        >
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            @click.stop="clearFilters"
                        >
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <div class="grid-wrapper">
                    <kendo-datasource
                        ref="datasource"
                        :transport="transportSettings"
                        :schema-model-id="'id'"
                        :schema-model-fields="schemaModelFields"
                        :schema-total="'total'"
                        :schema-data="'data'"
                        :page-size="defaultGridRows"
                        :server-paging="true"
                        :server-sorting="true"
                        :server-filtering="true"
                        :error="dataSourceError"
                        :requestEnd="datasorceRequestEnd"
                    ></kendo-datasource>

                    <kendo-grid
                        ref="grid"
                        :data-source-ref="'datasource'"
                        :pageable="gridOptions.pageable"
                        :navigatable="gridOptions.navigatable"
                        :editable="gridOptions.editable"
                        :filterable="gridOptions.filterable"
                        :reorderable="gridOptions.reorderable"
                        :resizable="gridOptions.resizable"
                        :groupable="gridOptions.groupable"
                        :allow-copy="gridOptions.allowCopy"
                        :column-menu="gridOptions.columnMenu"
                        :sortable-mode="gridOptions.sortable.mode"
                        :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                        :sortable-show-indexes="gridOptions.sortable.showIndexes"
                        :selectable="gridOptions.selectable"
                        :scrollable="gridOptions.scrollable"
                        :dataBound="dataBound"
                    >
                        <kendo-grid-column
                            :field="'id'"
                            :title="$t('participationRelationType.gridHeaders.id')"
                            :hidden="true"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            v-if="showBtns"
                            field="btnsColumn"
                            :title="$t('actions')"
                            :sortable="false"
                            :filterable="false"
                            :template="btnsTemplate"
                            :width="150"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'name'"
                            :title="$t('participationRelationType.gridHeaders.name')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'nameEn'"
                            :title="$t('participationRelationType.gridHeaders.nameEn')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'description'"
                            :title="$t('participationRelationType.gridHeaders.description')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'sort'"
                            :title="$t('participationRelationType.gridHeaders.sort')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'participationTypesStr'"
                            :hidden="isEnVersion"
                            :title="$t('participationRelationType.gridHeaders.participationTypes')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'participationTypesStrEn'"
                            :hidden="!isEnVersion"
                            :title="$t('participationRelationType.gridHeaders.participationTypesEn')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'active'"
                            :title="$t('participationRelationType.gridHeaders.active')"
                            :template="isActiveTemplate"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'createdAtUtc'"
                            :title="$t('createdOn')"
                            :sortable="false"
                            :format="dateTimeCustomFormat"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'creatorUsername'"
                            :title="$t('createdBy')"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'updatedAtUtc'"
                            :title="$t('lastUpdatedOn')"
                            :sortable="false"
                            :format="dateTimeCustomFormat"
                        ></kendo-grid-column>
                        <kendo-grid-column
                            :field="'updaterUsername'"
                            :title="$t('lastUpdatedBy')"
                        ></kendo-grid-column>
                    </kendo-grid>
                </div>
            </div>
        </div>

        <CreateModal :show="showCreate" @onCreate="onCreate" @onClose="onCreateClose"></CreateModal>
        <EditModal v-model="model" :show="showEdit" @onClose="onEditClose" @onSave="onEdit"></EditModal>
        <JsonFormatter
            v-model="dataReferences"
            :show.sync="showDataReferences"
            :titleKey="'buttons.links.tooltipText'"
            @onClose="onDataReferencesClose"
        >
        </JsonFormatter>
    </div>
</template>

<script>
import Vue from "vue";
import PageTitle from "@/components/common/PageTitle";
import CreateModal from "@/components/administration/participationRelationType/create.modal";
import EditModal from "@/components/administration/participationRelationType/edit.modal";
import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
import personalizationService from "@/services/personalization.service";
import participationRelationTypeService from "@/services/participationRelationType.service";
import dataReferencesService from "@/services/dataReferences.service";
import JsonFormatter from "@/components/common/json.formatter.modal.vue";
import { mapGetters } from "vuex";

export default {
    components: {
        PageTitle,
        CreateModal,
        EditModal,
        JsonFormatter
    },
    props: {
        showBtns: {
            default: true,
            type: Boolean,
        },
    },
    data() {
        return {
            showCreate: false,
            showEdit: false,
            model: null,
            searchQuery: "",
            schemaModelFields: {
                id: {
                    name: "id",
                    from: "id",
                    type: "number",
                    editable: false,
                    nullable: false,
                    visible: false,
                },
                name: {
                    name: "name",
                    from: "name",
                    type: "string",
                    editable: false,
                    nullable: false,
                    visible: true,
                },
                nameEn: {
                    name: "nameEn",
                    from: "nameEn",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                sort: {
                    name: "sort",
                    from: "sort",
                    type: "number",
                    editable: false,
                    nullable: false,
                    visible: false,
                },
                description: {
                    name: "description",
                    from: "description",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                deactivated: {
                    name: "deactivated",
                    from: "deactivated",
                    type: "boolean",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                active: {
                    name: "active",
                    from: "active",
                    type: "boolean",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                participationTypesStr: {
                    name: "participationTypesStr",
                    from: "participationTypesStr",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                participationTypesStrEn: {
                    name: "participationTypesStrEn",
                    from: "participationTypesStrEn",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                },
                creatorUsername: {
                    name: "creatorUsername",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                updaterUsername: {
                    name: "updaterUsername",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                createdAtUtc: {
                    name: "createdAtUtc",
                    type: "date",
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                updatedAtUtc: {
                    name: "updatedAtUtc",
                    type: "date",
                    editable: false,
                    nullable: true,
                    visible: true,
                },
            },
            transportSettings: {
                read: {
                    url:
                        this.$store.getters.baseUrl +
                        "/api/ParticipationRelationType/list",
                    dataType: "json",
                    type: "POST",
                    contentType: "application/json",
                    beforeSend: this.beforeSend,
                },
                parameterMap: this.parameterMap,
            },
            dataReferences: "",
            showDataReferences: false,
        };
    },
    methods: {
        dataBound(e) {},
        datasorceRequestEnd(e) {
            // e.type => ['read', 'create', 'update', destroy]
        },
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t("notifications.error"),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: "danger",
                time: this.$store.getters.notificationDuration,
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== "read" && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                return kendo.stringify(options);
            }
        },
        beforeSend: function (req) {
            if (this.authToken) {
                req.setRequestHeader("Authorization", "Bearer " + this.authToken);
            }
        },
        isActiveTemplate: function (dataItem) {
            let templateHtml =
                '<div class="text-center"><span class="badge ' +
                (dataItem.active ? "badge-success" : "badge-danger") +
                '">' +
                kendo.htmlEncode(
                    dataItem.active ? this.$t("yes") : this.$t("no")
                ) +
                "</span></div>";

            return templateHtml;
        },
        refresh() {
            let grid = this.$refs.grid;
            if (grid) {
                grid.kendoWidget().dataSource.read();
            }
        },
        clearFilters() {
            this.searchQuery = "";
            personalizationService.claerState(this.componentName);
            personalizationService.clearFilters(this.$refs.grid.kendoWidget());
        },
        massSearch() {
            if (!this.searchQuery) {
                this.$vs.notify({
                    title: "Search",
                    text: "Nothing to search for",
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });

                return;
            }

            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                let filter = this.$helpers.getMassFilterForGrid(
                    grid,
                    this.searchQuery
                );
                if (filter) {
                    grid.dataSource.filter(filter);
                }
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    parentComponent: this.$refs.grid,
                    btns: [
                        {
                            name: "edit",
                            text: "",
                            tooltip: this.$t("buttons.edit.btnText"),
                            class: "btn btn-primary",
                            icon: "fas fa-edit",
                            show: this.hasEditRights,
                            clickHandler: this.editBtnClick,
                        },
                        {
                            name: "deactivation",
                            text: "",
                            tooltip: this.$t("buttons.deactivate.btnText"),
                            class: "btn btn-danger",
                            icon: "fas fa-lock",
                            show: this.hasDeleteRights && !e.deactivated,
                            clickHandler: this.deactivateBtnClick,
                        },
                        {
                            name: "activation",
                            text: "",
                            tooltip: this.$t("buttons.activate.btnText"),
                            class: "btn btn-success",
                            icon: "fas fa-lock-open",
                            show: this.hasDeleteRights && e.deactivated,
                            clickHandler: this.activateBtnClick,
                        },
                        {
                            name: "checkLinks",
                            text: "",
                            tooltip: this.$t("buttons.links.btnText"),
                            class: "btn btn-info",
                            icon: "fa fa-link",
                            show: this.claims.dataReferences.read,
                            clickHandler: this.dataReferencesBtnClick,
                        },
                    ],
                },
            };
        },
        createBtnClick() {
            if (!this.hasCreateRights) {
                return this.$router.push(`/unauthorized`);
            }

            this.showCreateModal();
        },
        showCreateModal() {
            this.showCreate = true;
        },
        onCreate(model) {
            this.refresh();
        },
        onCreateClose() {
            this.showCreate = false;
        },
        editBtnClick(args) {
            if (!this.hasEditRights) {
                return this.$router.push(`/unauthorized`);
            }

            this.model = args;
            this.showEdit = true;
        },
        onEditClose() {
            this.showEdit = false;
            this.model = null;
        },
        onEdit(item) {
            this.refresh();
        },
        deactivateBtnClick(args) {
            if (!this.hasDeleteRights) {
                return this.$router.push(`/unauthorized`);
            }

            participationRelationTypeService
                .deactivate(args.id)
                .then((response) => {
                    this.refresh();

                    this.$vs.notify({
                        title: this.$t('buttons.deactivate.action'),
                        text: this.$helpers.responseMessage(response),
                        color: "success",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t('buttons.deactivate.action'),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        activateBtnClick(args) {
            if (!this.hasDeleteRights) {
                return this.$router.push(`/unauthorized`);
            }

            participationRelationTypeService
                .activate(args.id)
                .then((response) => {
                    this.refresh();

                    this.$vs.notify({
                        title: this.$t('buttons.activate.action'),
                        text: this.$helpers.responseMessage(response),
                        color: "success",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t('buttons.activate.action'),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        dataReferencesBtnClick(args) {
            if (!this.claims.dataReferences.read) {
                return this.$router.push(`/unauthorized`);
            }

            return this.getDataReferences(args.id);
        },
        getDataReferences(id) {
            dataReferencesService
                .checkReferences("dbo", "RegisterEntityRelationType", id, this.dataReferencesListCount, true)
                .then((response) => {
                    if (response.data) {
                        this.dataReferences = response.data;
                    }

                    this.showDataReferences = true;
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        onDataReferencesClose() {
            this.dataReferences = "";
            this.showDataReferences = false;
        },
    },
    computed: {
        ...mapGetters(['gridOptions', 'claims', 'authToken', 'isEnVersion', 'dateTimeLongFormat', 'defaultGridRows', 'dataReferencesListCount']),
        dateTimeCustomFormat() {
            return `{0: ${this.dateTimeLongFormat} }`;
        },
        hasCreateRights() {
            return this.claims.nomenclatureTypes.create;
        },
        hasEditRights() {
            return this.claims.nomenclatureTypes.update;
        },
        hasDeleteRights() {
            return this.claims.nomenclatureTypes.delete;
        },
    },
    mounted() {},
};
</script>

<style>
</style>
